import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { ChecksConfigSection } from "@/components/ChecksConfig";

interface Project {
  id: string;
  slug: string;
  githubRepo: string;
  framework: "vite" | "next";
  rootDirectory: string;
  installCommand: string | null;
  buildCommand: string | null;
  outputDirectory: string | null;
  prodBranch: string;
  stagingBranch: string | null;
  developmentBranch: string | null;
  packageManager: "pnpm" | "yarn" | "npm";
  nodeVersion: string;
  prCommentsEnabled: boolean;
  prCommentsOnFailureOnly: boolean;
  ignoredBuildCommand: string | null;
  branchIgnorePatterns: string[];
  filesChangedFilter: string[];
  speedInsightsEnabled: boolean;
  rollingReleasesEnabled: boolean;
  rollingStages: Array<{ percent: number; durationSec: number | null }>;
}

type PrCommentMode = "all" | "failures-only" | "off";

function modeOf(p: { prCommentsEnabled: boolean; prCommentsOnFailureOnly: boolean }): PrCommentMode {
  if (!p.prCommentsEnabled) return "off";
  return p.prCommentsOnFailureOnly ? "failures-only" : "all";
}

function flagsOf(mode: PrCommentMode): { prCommentsEnabled: boolean; prCommentsOnFailureOnly: boolean } {
  if (mode === "off") return { prCommentsEnabled: false, prCommentsOnFailureOnly: false };
  if (mode === "failures-only") return { prCommentsEnabled: true, prCommentsOnFailureOnly: true };
  return { prCommentsEnabled: true, prCommentsOnFailureOnly: false };
}

const DEFAULT_INSTALL: Record<Project["packageManager"], string> = {
  pnpm: "pnpm install --frozen-lockfile",
  yarn: "yarn install --frozen-lockfile",
  npm:  "npm ci",
};
const DEFAULT_BUILD: Record<Project["packageManager"], string> = {
  pnpm: "pnpm run build",
  yarn: "yarn run build",
  npm:  "npm run build",
};
const DEFAULT_OUTPUT: Record<Project["framework"], string> = {
  vite: "dist",
  next: ".next/standalone",
};

export function ProjectSettings() {
  const { org, project: projectSlug } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["project", org, projectSlug],
    queryFn: () => api.get<{ project: Project }>(`/orgs/${org}/projects/${projectSlug}`).then((r) => r.project),
  });

  // Local form state, initialized from server on first load. We don't bind
  // form fields directly to query data because users mid-edit shouldn't get
  // their typing stomped by background refetches.
  const [form, setForm] = useState<Partial<Project>>({});
  useEffect(() => {
    if (q.data && Object.keys(form).length === 0) setForm(q.data);
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ project: Project }>(`/orgs/${org}/projects/${projectSlug}`, {
        rootDirectory: form.rootDirectory,
        installCommand: emptyToNull(form.installCommand),
        buildCommand: emptyToNull(form.buildCommand),
        outputDirectory: emptyToNull(form.outputDirectory),
        prodBranch: form.prodBranch,
        stagingBranch: emptyToNull(form.stagingBranch),
        developmentBranch: emptyToNull(form.developmentBranch),
        nodeVersion: form.nodeVersion,
        packageManager: form.packageManager,
        prCommentsEnabled: form.prCommentsEnabled,
        prCommentsOnFailureOnly: form.prCommentsOnFailureOnly,
        ignoredBuildCommand: form.ignoredBuildCommand ?? "",
        branchIgnorePatterns: form.branchIgnorePatterns ?? [],
        filesChangedFilter: form.filesChangedFilter ?? [],
        speedInsightsEnabled: form.speedInsightsEnabled ?? false,
        rollingReleasesEnabled: form.rollingReleasesEnabled ?? false,
        rollingStages: form.rollingStages ?? [],
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["project", org, projectSlug] });
      setForm(r.project);
    },
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/orgs/${org}/projects/${projectSlug}`, { confirm: projectSlug! }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", org] });
      window.location.href = `/orgs/${org}`;
    },
  });

  if (q.isLoading || !q.data) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  const p = q.data;
  const dirty = JSON.stringify(form) !== JSON.stringify(p);

  return (
    <div className="space-y-5">
      <Section title="General">
        <Row label="GitHub repository">
          <span className="font-mono text-sm break-all">{p.githubRepo}</span>
        </Row>
        <Row label="Framework">
          <span className="text-2xs uppercase tracking-wider font-mono bg-subtle border border-border rounded px-1.5 py-0.5">
            {p.framework}
          </span>
        </Row>
      </Section>

      <Section title="Build configuration">
        <Field
          label="Root directory"
          help="Sub-path within the repo (use / for monorepo root)."
          value={form.rootDirectory ?? "/"}
          onChange={(v) => setForm((f) => ({ ...f, rootDirectory: v }))}
          mono
        />
        <SelectField
          label="Package manager"
          value={form.packageManager ?? "npm"}
          options={[
            { value: "pnpm", label: "pnpm" },
            { value: "yarn", label: "yarn" },
            { value: "npm", label: "npm" },
          ]}
          onChange={(v) => setForm((f) => ({ ...f, packageManager: v as Project["packageManager"] }))}
        />
        <SelectField
          label="Node version"
          help="Build container Node major. pnpm@>=11 needs 22+."
          value={form.nodeVersion ?? "22"}
          options={[
            { value: "18", label: "18 (LTS)" },
            { value: "20", label: "20 (LTS)" },
            { value: "22", label: "22 (LTS, recommended)" },
          ]}
          onChange={(v) => setForm((f) => ({ ...f, nodeVersion: v }))}
        />
        <Field
          label="Install command"
          help={`Default: ${DEFAULT_INSTALL[form.packageManager ?? "npm"]}`}
          value={form.installCommand ?? ""}
          placeholder={DEFAULT_INSTALL[form.packageManager ?? "npm"]}
          onChange={(v) => setForm((f) => ({ ...f, installCommand: v || null }))}
          mono
        />
        <Field
          label="Build command"
          help={`Default: ${DEFAULT_BUILD[form.packageManager ?? "npm"]}`}
          value={form.buildCommand ?? ""}
          placeholder={DEFAULT_BUILD[form.packageManager ?? "npm"]}
          onChange={(v) => setForm((f) => ({ ...f, buildCommand: v || null }))}
          mono
        />
        <Field
          label="Output directory"
          help={`Relative to root directory. Default: ${DEFAULT_OUTPUT[p.framework]}`}
          value={form.outputDirectory ?? ""}
          placeholder={DEFAULT_OUTPUT[p.framework]}
          onChange={(v) => setForm((f) => ({ ...f, outputDirectory: v || null }))}
          mono
        />
        <Field
          label="Ignored build step"
          help="Runs before install. Exit 0 = skip the deploy, anything else = build normally. Single line, no newlines."
          value={form.ignoredBuildCommand ?? ""}
          placeholder='e.g. git diff --quiet HEAD^ HEAD -- ./docs'
          onChange={(v) => setForm((f) => ({ ...f, ignoredBuildCommand: v || null }))}
          mono
        />
        <Field
          label="Only build when paths change"
          help="Comma-separated globs. A push whose changed files match none of these is skipped (recorded as a skipped deployment). Empty = always build."
          value={(form.filesChangedFilter ?? []).join(", ")}
          placeholder="apps/web/**, packages/**"
          onChange={(v) => setForm((f) => ({ ...f, filesChangedFilter: csvToList(v) }))}
          mono
        />
        <Field
          label="Ignored branches"
          help="Comma-separated branch patterns that never trigger builds (e.g. renovate/**, docs-*)."
          value={(form.branchIgnorePatterns ?? []).join(", ")}
          placeholder="renovate/**, dependabot/**"
          onChange={(v) => setForm((f) => ({ ...f, branchIgnorePatterns: csvToList(v) }))}
          mono
        />
      </Section>

      <Section title="Speed insights">
        <Row label="Real-user Core Web Vitals">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.speedInsightsEnabled ?? false}
              onChange={(e) => setForm((f) => ({ ...f, speedInsightsEnabled: e.target.checked }))}
            />
            <span className="text-sm">
              Collect LCP / CLS / INP / FCP / TTFB from real visits.
              <span className="block text-2xs text-subtle-foreground mt-0.5">
                Static builds get the collector injected into their HTML; server builds are wrapped at
                deploy time. Applies from the next deployment. View results on the Analytics tab.
              </span>
            </span>
          </label>
        </Row>
      </Section>

      <Section title="GitHub">
        <PrCommentsField
          mode={modeOf({
            prCommentsEnabled: form.prCommentsEnabled ?? p.prCommentsEnabled,
            prCommentsOnFailureOnly: form.prCommentsOnFailureOnly ?? p.prCommentsOnFailureOnly,
          })}
          onChange={(mode) => {
            const flags = flagsOf(mode);
            setForm((f) => ({ ...f, ...flags }));
          }}
        />
      </Section>

      <Section title="Rolling releases">
        <Row label="Canary production deploys">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.rollingReleasesEnabled ?? false}
              onChange={(e) => setForm((f) => ({ ...f, rollingReleasesEnabled: e.target.checked }))}
            />
            <span className="text-sm">
              New production builds start at a small traffic share and roll out in stages.
              <span className="block text-2xs text-subtle-foreground mt-0.5">
                Abort returns 100% to the previous build instantly. A canary 5xx spike auto-aborts.
                The final 100% stage is implicit.
              </span>
            </span>
          </label>
        </Row>
        {(form.rollingReleasesEnabled ?? false) && (
          <Row label="Stages">
            <div className="space-y-1.5">
              {(form.rollingStages ?? []).map((s, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={99}
                    value={s.percent}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        rollingStages: (f.rollingStages ?? []).map((x, j) =>
                          j === i ? { ...x, percent: Math.max(1, Math.min(99, Math.round(Number(e.target.value) || 0))) } : x,
                        ),
                      }))
                    }
                    className="h-8 w-20 font-mono"
                  />
                  <span className="text-xs text-muted-foreground">% for</span>
                  <select
                    value={s.durationSec === null ? "manual" : String(s.durationSec)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        rollingStages: (f.rollingStages ?? []).map((x, j) =>
                          j === i
                            ? { ...x, durationSec: e.target.value === "manual" ? null : Number(e.target.value) }
                            : x,
                        ),
                      }))
                    }
                    className="h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
                  >
                    <option value="manual">until approved</option>
                    <option value="60">1 minute</option>
                    <option value="300">5 minutes</option>
                    <option value="600">10 minutes</option>
                    <option value="1800">30 minutes</option>
                    <option value="3600">1 hour</option>
                  </select>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setForm((f) => ({ ...f, rollingStages: (f.rollingStages ?? []).filter((_, j) => j !== i) }))
                    }
                    title="Remove stage"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" />
                  </Button>
                </div>
              ))}
              {(form.rollingStages ?? []).length < 5 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() =>
                    setForm((f) => {
                      const cur = f.rollingStages ?? [];
                      const last = cur[cur.length - 1]?.percent ?? 0;
                      return {
                        ...f,
                        rollingStages: [...cur, { percent: Math.min(99, last + 25 || 10), durationSec: 300 }],
                      };
                    })
                  }
                >
                  Add stage
                </Button>
              )}
              <div className="text-2xs text-subtle-foreground">
                Percents must increase stage to stage; the release completes at the implicit 100%.
              </div>
            </div>
          </Row>
        )}
      </Section>

      <ChecksConfigSection orgSlug={org!} projectSlug={projectSlug!} />

      <Section title="Branches">
        <Field
          label="Production branch"
          help="Pushes here deploy to env=production."
          value={form.prodBranch ?? ""}
          onChange={(v) => setForm((f) => ({ ...f, prodBranch: v }))}
          mono
        />
        <Field
          label="Staging branch (optional)"
          help="Pushes here deploy to env=staging."
          value={form.stagingBranch ?? ""}
          placeholder="e.g. staging"
          onChange={(v) => setForm((f) => ({ ...f, stagingBranch: v || null }))}
          mono
        />
        <Field
          label="Development branch (optional)"
          help="Pushes here deploy to env=development."
          value={form.developmentBranch ?? ""}
          placeholder="e.g. develop"
          onChange={(v) => setForm((f) => ({ ...f, developmentBranch: v || null }))}
          mono
        />
      </Section>

      <div className="flex flex-wrap items-center justify-between gap-3 gap-y-2 sticky bottom-0 bg-background/90 backdrop-blur border-t border-border-muted px-4 py-3 -mx-4">
        <div className="text-xs text-muted-foreground">
          {save.isSuccess && !dirty && (
            <span className="inline-flex items-center gap-1 text-success">
              <CheckCircle2 className="h-3.5 w-3.5" /> Saved. New builds use these settings.
            </span>
          )}
          {save.error instanceof ApiError && (
            <span className="text-danger">{save.error.message}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            onClick={() => setForm(p)}
            disabled={!dirty || save.isPending}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save changes
          </Button>
        </div>
      </div>

      <Section title="Danger zone" tone="danger">
        <div className="p-4 space-y-2">
          <div className="text-sm">Delete this project</div>
          <div className="text-xs text-muted-foreground">
            Soft-deletes the project. All deployments, env vars, and routes are removed.
            Recoverable for 30 days via API restore endpoint.
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              if (confirm(`Delete ${p.slug}? Type the slug to confirm.`)) del.mutate();
            }}
            disabled={del.isPending}
            className="text-danger border-danger/40 hover:bg-danger-subtle"
          >
            {del.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Delete project
          </Button>
        </div>
      </Section>
    </div>
  );
}

function csvToList(v: string): string[] {
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 50);
}

function emptyToNull(v: string | null | undefined): string | null {
  if (v == null) return null;
  const trimmed = v.trim();
  return trimmed === "" ? null : trimmed;
}

function Section({ title, tone, children }: { title: string; tone?: "danger"; children: React.ReactNode }) {
  return (
    <div className={"of-card" + (tone === "danger" ? " border-danger/30" : "")}>
      <div className={"of-card-header" + (tone === "danger" ? " bg-danger-subtle/50" : "")}>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="divide-y divide-border-muted">{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3 items-center">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div>{children}</div>
    </div>
  );
}

function Field({
  label, help, value, placeholder, onChange, mono,
}: {
  label: string;
  help?: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) {
  return (
    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
      <div className="space-y-0.5">
        <Label className="text-xs">{label}</Label>
        {help && <div className="text-2xs text-subtle-foreground">{help}</div>}
      </div>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={mono ? "font-mono text-sm" : "text-sm"}
      />
    </div>
  );
}

function SelectField({
  label, help, value, options, onChange,
}: {
  label: string;
  help?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
      <div className="space-y-0.5">
        <Label className="text-xs">{label}</Label>
        {help && <div className="text-2xs text-subtle-foreground">{help}</div>}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-3 rounded-md border border-border bg-background text-sm font-mono"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function PrCommentsField({
  mode,
  onChange,
}: {
  mode: PrCommentMode;
  onChange: (m: PrCommentMode) => void;
}) {
  const options: Array<{ value: PrCommentMode; label: string; help: string }> = [
    { value: "all", label: "All", help: "Comment on every PR build (queued, building, ready, failed)." },
    { value: "failures-only", label: "Failures only", help: "Only post when a build fails; on later success, update the existing comment to mark it resolved." },
    { value: "off", label: "Off", help: "Never post PR comments for this project." },
  ];
  return (
    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
      <div className="space-y-0.5">
        <Label className="text-xs">PR comments</Label>
        <div className="text-2xs text-subtle-foreground">
          Controls the sticky preview comment we post on pull requests.
        </div>
      </div>
      <div role="radiogroup" aria-label="PR comments mode" className="space-y-2">
        {options.map((o) => (
          <label
            key={o.value}
            className="flex items-start gap-2 cursor-pointer text-sm"
          >
            <input
              type="radio"
              name="pr-comments-mode"
              value={o.value}
              checked={mode === o.value}
              onChange={() => onChange(o.value)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">{o.label}</span>
              <span className="block text-2xs text-subtle-foreground">{o.help}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
